import { useState } from 'react'
import { ChevronDown, ChevronUp, Copy, Phone, User, X, Trash2, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { DuplicateGroup } from '../../api/campaigns'
import { useResolveDuplicates, useExcludeCampaignContacts } from '../../api/campaigns'
import { useStatusLabel } from '../../i18n/statusLabels'
import { useDispositionLabel } from '../../i18n/dispositionLabels'
import { formatPhone, cn } from '../../lib/utils'

interface DuplicatesPanelProps {
  groups: DuplicateGroup[]
  totalDuplicates: number
  listId: number
  onResolved?: () => void
}

function ContactRow({ contact, ci }: {
  contact: { id: number; name: string; phone: string; status: string; disposition: string | null }
  ci: number
}) {
  const { t } = useTranslation('campaigns')
  const statusLabel = useStatusLabel(contact.status, 'campaignContact')
  const dispositionLabel = useDispositionLabel(contact.disposition ?? '')
  return (
    <tr
      className={cn(
        'border-t border-border/50',
        ci === 0 && 'bg-success/5',
      )}
    >
      <td className="px-3 py-2 text-text-muted">#{contact.id}</td>
      <td className="px-3 py-2 text-text-primary">{contact.name || '—'}</td>
      <td className="px-3 py-2 font-mono text-text-secondary">
        {formatPhone(contact.phone)}
      </td>
      <td className="px-3 py-2">
        <span
          className={cn(
            'inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium',
            contact.status === 'called'
              ? 'bg-success/10 text-success'
              : contact.status === 'skipped'
                ? 'bg-text-muted/10 text-text-muted'
                : 'bg-primary/10 text-primary',
          )}
        >
          {statusLabel}
        </span>
      </td>
      <td className="px-3 py-2 text-text-muted">
        {contact.disposition ? dispositionLabel : '—'}
      </td>
      <td className="px-3 py-2 text-right">
        {ci === 0 ? (
          <span className="text-[10px] font-medium text-success">{t('duplicates.keep')}</span>
        ) : (
          <span className="text-[10px] font-medium text-error">{t('duplicates.remove')}</span>
        )}
      </td>
    </tr>
  )
}

export function DuplicatesPanel({ groups, totalDuplicates, listId, onResolved }: DuplicatesPanelProps) {
  const { t } = useTranslation('campaigns')
  const [expanded, setExpanded] = useState(true)
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set())
  const [confirmAll, setConfirmAll] = useState(false)

  const resolveMutation = useResolveDuplicates()
  const excludeMutation = useExcludeCampaignContacts()

  const isBusy = resolveMutation.isPending || excludeMutation.isPending

  const toggleGroup = (index: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const handleResolveAll = () => {
    if (!confirmAll) {
      setConfirmAll(true)
      return
    }
    resolveMutation.mutate(listId, {
      onSuccess: () => {
        setConfirmAll(false)
        onResolved?.()
      },
    })
  }

  const handleExcludeGroup = (group: DuplicateGroup) => {
    // Keep first (lowest ID), exclude the rest
    const sorted = [...group.contacts].sort((a, b) => a.id - b.id)
    const idsToExclude = sorted.slice(1).map((c) => c.id)
    if (idsToExclude.length === 0) return
    excludeMutation.mutate(idsToExclude, {
      onSuccess: () => onResolved?.(),
    })
  }

  return (
    <div className="rounded-lg border border-warning/30 bg-warning/5">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <Copy className="h-4 w-4 text-warning" />
          <span className="text-sm font-semibold text-text-primary">
            {t('duplicates.groupsFound', { count: groups.length })}
          </span>
          <span className="text-xs text-text-muted">
            {t('duplicates.totalContacts', { count: totalDuplicates })}
          </span>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-text-muted" />
          ) : (
            <ChevronDown className="h-4 w-4 text-text-muted" />
          )}
        </button>

        {/* Bulk resolve button */}
        {expanded && (
          <button
            onClick={handleResolveAll}
            disabled={isBusy}
            className={cn(
              'ml-3 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
              confirmAll
                ? 'bg-error text-white hover:bg-error/90'
                : 'bg-warning/10 text-warning hover:bg-warning/20',
              isBusy && 'cursor-not-allowed opacity-50',
            )}
          >
            {resolveMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            {confirmAll ? t('duplicates.confirmRemoveAll') : t('duplicates.removeAll')}
          </button>
        )}
      </div>

      {/* Body */}
      {expanded && (
        <div className="space-y-2 px-4 pb-4">
          {confirmAll && (
            <div className="flex items-center justify-between rounded-lg border border-error/30 bg-error/5 px-3 py-2 text-xs text-text-secondary">
              <span>
                {t('duplicates.confirmHint')}
              </span>
              <button
                onClick={() => setConfirmAll(false)}
                className="ml-2 text-text-muted hover:text-text-primary"
              >
                {t('common:actions.cancel')}
              </button>
            </div>
          )}

          {groups.map((group, i) => (
            <div
              key={`${group.name}-${group.phone}-${i}`}
              className="rounded-lg border border-border bg-surface-card"
            >
              {/* Group header */}
              <div className="flex items-center justify-between px-3 py-2.5">
                <button
                  onClick={() => toggleGroup(i)}
                  className="flex flex-1 items-center gap-3 text-left"
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-warning/10">
                    <User className="h-3.5 w-3.5 text-warning" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-text-primary">
                      {group.name || t('duplicates.noName')}
                    </span>
                    <span className="ml-2 inline-flex items-center gap-1 font-mono text-xs text-text-secondary">
                      <Phone className="h-3 w-3" />
                      {formatPhone(group.phone)}
                    </span>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
                    {group.count}×
                  </span>
                  <button
                    onClick={() => handleExcludeGroup(group)}
                    disabled={isBusy}
                    title={t('duplicates.removeGroup')}
                    className={cn(
                      'rounded-lg border border-border p-1.5 text-text-muted transition-colors hover:border-error/50 hover:bg-error/10 hover:text-error',
                      isBusy && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    {excludeMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <X className="h-3.5 w-3.5" />
                    )}
                  </button>
                  {expandedGroups.has(i) ? (
                    <ChevronUp className="h-3.5 w-3.5 text-text-muted" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5 text-text-muted" />
                  )}
                </div>
              </div>

              {/* Group contacts */}
              {expandedGroups.has(i) && (
                <div className="border-t border-border">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-text-muted">
                        <th className="px-3 py-2">{t('duplicates.table.id')}</th>
                        <th className="px-3 py-2">{t('duplicates.table.name')}</th>
                        <th className="px-3 py-2">{t('duplicates.table.phone')}</th>
                        <th className="px-3 py-2">{t('duplicates.table.status')}</th>
                        <th className="px-3 py-2">{t('duplicates.table.disposition')}</th>
                        <th className="px-3 py-2 w-16"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...group.contacts]
                        .sort((a, b) => a.id - b.id)
                        .map((contact, ci) => (
                          <ContactRow key={contact.id} contact={contact} ci={ci} />
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
